import { useCallback, useEffect, useState } from "react";
import { RefreshCw, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import AdminShell from "../../components/AdminShell";
import api, { formatApiError } from "../../lib/api";
import { LoadingState, ErrorState } from "../../components/LoadingState";
import { ActionButton, MetricCard, StatusBadge, TableWrapper } from "../../components/customer/CustomerUI";

export default function JobManagement() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const load = useCallback(() => { setError(false); return api.get("/admin/jobs").then((r) => setData(r.data)).catch(() => setError(true)); }, []);
  useEffect(() => { load(); const timer = setInterval(load, 30000); return () => clearInterval(timer); }, [load]);

  const retry = async (id) => {
    try { await api.post(`/admin/jobs/${id}/retry`); toast.success("Retry queued"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const q = data?.queue || {};
  const heartbeat = data?.scheduler || {};
  const workers = data?.workers || {};
  if (!data && !error) return <AdminShell title="Scheduler & Job Management" subtitle="Platform scheduler, queue health, failures, and retries"><LoadingState cards={4} rows={4} label="Loading scheduler and jobs" /></AdminShell>;
  if (!data && error) return <AdminShell title="Scheduler & Job Management" subtitle="Platform scheduler, queue health, failures, and retries"><ErrorState message="Scheduler data is temporarily unavailable." onRetry={load} /></AdminShell>;
  return (
    <AdminShell title="Scheduler & Job Management" subtitle="Platform scheduler, queue health, failures, and retries">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <MetricCard label="Scheduler" value={<StatusBadge tone={heartbeat.last_error ? "danger" : "success"}>{heartbeat.last_error ? "Degraded" : "Operational"}</StatusBadge>} labelClassName="font-mono text-[10px] uppercase tracking-wider text-slate-500" />
        <MetricCard label="Active Jobs" value={q.active ?? "—"} labelClassName="font-mono text-[10px] uppercase tracking-wider text-slate-500" />
        <MetricCard label="Failed Jobs" value={q.failed ?? "—"} labelClassName="font-mono text-[10px] uppercase tracking-wider text-slate-500" />
        <MetricCard label="Scheduled CRMs" value={q.scheduled_connections ?? "—"} labelClassName="font-mono text-[10px] uppercase tracking-wider text-slate-500" />
      </div>
      <div className="mt-3 text-xs text-slate-500">Durable workers: {workers.workers ?? "—"} · Active: {workers.active ?? "—"} · Recovered leases: {workers.recovered ?? "—"} · Lease: {workers.lease_minutes ?? "—"}m</div>
      <div className="mt-6 flex items-center justify-between">
        <div>
          <h2 className="font-heading text-lg font-bold text-slate-900">Failed jobs</h2>
          <p className="text-xs text-slate-500">Last scheduler tick: {heartbeat.last_tick_at ? new Date(heartbeat.last_tick_at).toLocaleString() : "Starting…"}</p>
        </div>
        <ActionButton variant="secondary" onClick={load}><RefreshCw className="h-3.5 w-3.5" /> Refresh</ActionButton>
      </div>
      <TableWrapper className="mt-3">
        {(data?.failed_jobs || []).length === 0 ? <div className="p-8 text-center text-sm text-slate-500">No failed jobs.</div> : (
          <table className="w-full text-left text-sm">
            <thead className="border-b border-slate-300 font-mono text-[10px] uppercase tracking-wider text-slate-500"><tr><th className="p-3">CRM</th><th className="p-3">Started</th><th className="p-3">Error</th><th className="p-3"></th></tr></thead>
            <tbody>{data.failed_jobs.map((job) => <tr key={job.id} className="border-b border-slate-300 last:border-0">
              <td className="p-3 text-slate-900">{job.connector_name}</td><td className="p-3 text-slate-600">{new Date(job.started_at).toLocaleString()}</td>
              <td className="p-3 text-red-700">{job.error || "Scan failed"}</td><td className="p-3 text-right"><ActionButton variant="secondary" onClick={() => retry(job.id)}><RotateCcw className="h-3 w-3" /> Retry</ActionButton></td>
            </tr>)}</tbody>
          </table>
        )}
      </TableWrapper>
    </AdminShell>
  );
}
